// Dok 09 – Frånkoppling och återinkoppling (LOTO). El, tryck, gas och media som
// måste göras spänningslöst/trycklöst innan arbete. ELSÄK-FS 2022:1, AFS 2023:11.
import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import DocumentHeader from "./DocumentHeader";
import { Zap, Plus, Trash2, AlertTriangle, FileDown } from "lucide-react";
import { generateIsolationsWordDocument } from "@/lib/documentGenerator/basuExecutionGenerators";

interface Props {
  projectId: string;
}

interface Row {
  id: string;
  system_name: string;
  isolation_point: string | null;
  isolation_type: string;
  lock_id: string | null;
  isolated_by: string | null;
  isolated_at: string | null;
  verified_dead_by: string | null;
  restored_by: string | null;
  restored_at: string | null;
  status: string;
  notes: string | null;
}

const TYPES = [
  { value: "el", label: "El" },
  { value: "tryck", label: "Tryck/hydraulik" },
  { value: "gas", label: "Gas" },
  { value: "media", label: "Vatten/media" },
  { value: "mekanisk", label: "Mekanisk låsning" },
];

const STATUSES = [
  { value: "planned", label: "Planerad" },
  { value: "isolated", label: "Frånkopplad och låst" },
  { value: "restored", label: "Återinkopplad" },
];

const toLocal = (iso: string | null) => (iso ? iso.slice(0, 16) : "");

const ElectricalIsolations: React.FC<Props> = ({ projectId }) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("electrical_isolations")
      .select(
        "id, system_name, isolation_point, isolation_type, lock_id, isolated_by, isolated_at, verified_dead_by, restored_by, restored_at, status, notes"
      )
      .eq("project_id", projectId)
      .order("created_at", { ascending: true });
    if (error) toast.error("Kunde inte läsa frånkopplingarna");
    setRows((data as Row[] | null) ?? []);
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    const { data: auth } = await supabase.auth.getUser();
    const { error } = await supabase.from("electrical_isolations").insert({
      project_id: projectId,
      system_name: "",
      created_by: auth.user?.id ?? null,
    });
    if (error) return toast.error("Kunde inte lägga till raden");
    load();
  };

  const patch = async (id: string, p: Partial<Row>) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...p } : r)));
    const { error } = await supabase.from("electrical_isolations").update(p).eq("id", id);
    if (error) toast.error("Kunde inte spara");
  };

  const remove = async (id: string) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
    await supabase.from("electrical_isolations").delete().eq("id", id);
  };

  const isolatedNow = rows.filter((r) => r.status === "isolated");
  const missingVerification = isolatedNow.filter((r) => !r.verified_dead_by);

  if (loading) return <p className="py-10 text-center text-muted-foreground">Laddar…</p>;

  const exportWord = async () => {
    try {
      await generateIsolationsWordDocument(projectId, rows);
    } catch {
      toast.error("Kunde inte skapa Word-dokumentet");
    }
  };

  return (
    <div className="space-y-4">
      <DocumentHeader
        docNumber="09"
        icon={Zap}
        title="Frånkoppling och återinkoppling"
        description="Anläggningsdelar som ska göras spänningslösa, trycklösa eller mekaniskt låsta innan arbetet startar – vem som kopplade från, vem som mätte att det är spänningslöst och vem som kopplar in igen."
        legalRef="ELSÄK-FS 2022:1, AFS 2023:11"
        projectId={projectId}
        exportHistory={{ documentType: "electrical_isolation" }}
        meta={
          <Badge variant={missingVerification.length ? "destructive" : "secondary"}>
            {isolatedNow.length} frånkopplade · {missingVerification.length} utan mätning
          </Badge>
        }
        actions={
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={exportWord}>
              <FileDown className="mr-1 h-4 w-4" /> Exportera Dok 09
            </Button>
            <Button size="sm" variant="outline" onClick={add}>
              <Plus className="mr-1 h-4 w-4" /> Lägg till
            </Button>
          </div>
        }
      />

      {missingVerification.length > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Spänningslöshet är inte kontrollerad</AlertTitle>
          <AlertDescription className="text-sm">
            {missingVerification.length} frånkopplingar saknar uppgift om vem som mätt och
            konstaterat att anläggningen är spänningslös. Arbetet får inte påbörjas förrän det är
            gjort och antecknat.
          </AlertDescription>
        </Alert>
      )}

      {rows.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            Inga frånkopplingar registrerade. Lägg till en rad per anläggningsdel som ska kopplas
            från inför arbetet.
          </CardContent>
        </Card>
      )}

      {rows.map((r) => (
        <Card key={r.id}>
          <CardHeader className="flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-base">{r.system_name || "Ny frånkoppling"}</CardTitle>
            <Button size="sm" variant="ghost" onClick={() => remove(r.id)}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-3">
            <div>
              <Label>Anläggningsdel</Label>
              <Input
                value={r.system_name}
                onChange={(e) => patch(r.id, { system_name: e.target.value })}
                placeholder="T.ex. ställverk H2, grupp 12"
              />
            </div>
            <div>
              <Label>Frånskiljningspunkt</Label>
              <Input
                value={r.isolation_point ?? ""}
                onChange={(e) => patch(r.id, { isolation_point: e.target.value })}
                placeholder="T.ex. brytare i undercentral"
              />
            </div>
            <div>
              <Label>Typ</Label>
              <Select
                value={r.isolation_type}
                onValueChange={(v) => patch(r.id, { isolation_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Låsnummer</Label>
              <Input
                value={r.lock_id ?? ""}
                onChange={(e) => patch(r.id, { lock_id: e.target.value })}
              />
            </div>
            <div>
              <Label>Frånkopplad av</Label>
              <Input
                value={r.isolated_by ?? ""}
                onChange={(e) => patch(r.id, { isolated_by: e.target.value })}
              />
            </div>
            <div>
              <Label>Tidpunkt</Label>
              <Input
                type="datetime-local"
                value={toLocal(r.isolated_at)}
                onChange={(e) =>
                  patch(r.id, {
                    isolated_at: e.target.value ? new Date(e.target.value).toISOString() : null,
                  })
                }
              />
            </div>
            <div>
              <Label>Spänningslöshet kontrollerad av</Label>
              <Input
                value={r.verified_dead_by ?? ""}
                onChange={(e) => patch(r.id, { verified_dead_by: e.target.value })}
                placeholder="Namn på den som mätte"
              />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={r.status} onValueChange={(v) => patch(r.id, { status: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Återinkopplad av</Label>
              <Input
                value={r.restored_by ?? ""}
                onChange={(e) => patch(r.id, { restored_by: e.target.value })}
              />
            </div>
            <div>
              <Label>Återinkopplad</Label>
              <Input
                type="datetime-local"
                value={toLocal(r.restored_at)}
                onChange={(e) =>
                  patch(r.id, {
                    restored_at: e.target.value ? new Date(e.target.value).toISOString() : null,
                  })
                }
              />
            </div>
            <div className="sm:col-span-2">
              <Label>Anteckning</Label>
              <Input
                value={r.notes ?? ""}
                onChange={(e) => patch(r.id, { notes: e.target.value })}
              />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default ElectricalIsolations;
